import { baseSchema, Update } from './base'
import { generate } from './generate'
import { z } from 'zod'
import { inviteSchema } from './invite-base'

/* -------------------------------------------------------------------------- */
/*                                Plan Manager                                */
/* -------------------------------------------------------------------------- */

export const plans = [
  // LEGACY
  'pro',
  // CURRENT
  'personal',
  'team',
  'team_v2',
  'unlimited',
] as const
export type Plan = (typeof plans)[number]

// Overload
export function planSwitcher<T>(
  team: Pick<Team, 'plan' | 'plan_v2'>,
  cases: Partial<Record<Plan, () => T>> & { default: () => T }
): T
export function planSwitcher<T>(
  team: Pick<Team, 'plan' | 'plan_v2'>,
  cases: Partial<Record<Plan, () => T>>
): T | undefined
export function planSwitcher<T>(
  team: Pick<Team, 'plan' | 'plan_v2'>,
  cases: Record<Plan, () => T>
): T
// Implementation
export function planSwitcher<T>(
  team: Pick<Team, 'plan' | 'plan_v2'>,
  cases: Partial<Record<Plan | 'default', () => T>>
): T | undefined {
  const plan = team.plan_v2?.type || team.plan
  let callback: (() => T) | undefined

  if (plan && plan in cases) {
    callback = cases[plan as Plan]
  }

  if (!callback && 'default' in cases) {
    callback = cases.default
  }

  return callback ? callback() : undefined
}
/* -------------------------------------------------------------------------- */
/*                                   Members                                  */
/* -------------------------------------------------------------------------- */

export const roleSchema = z.enum(['owner', 'member', 'editor', 'viewer'])
export const RoleEnum = roleSchema.enum
export type Role = z.infer<typeof roleSchema>

export const memberSchema = z.object({
  userId: z.string(),
  createdAt: z.string(),
  role: roleSchema,
  hasAllAccess: z.boolean(),
  repoIds: z.array(z.string()).nullish(),
})

export type Member = z.infer<typeof memberSchema>

/* -------------------------------------------------------------------------- */
/*                                   Invites                                  */
/* -------------------------------------------------------------------------- */

export const teamInviteSchema = inviteSchema.extend({
  role: roleSchema,
  // NOTE: These keys are not in use, since invites are all-access by default,
  // but could be used to do invitations at repo level in the future.
  hasAllAccess: z.boolean(),
  repoIds: z.array(z.string()).nullish(),
})
export type TeamInvite = z.infer<typeof teamInviteSchema>

/* -------------------------------------------------------------------------- */
/*                          Subscriptions And Limits                          */
/* -------------------------------------------------------------------------- */

const usageRecordSchema = z.object({
  status: z.enum(['active', 'frozen']),
  limit: z.number().nullable(),
  // usage goes in the db or gets calculated on the fly.
})
export type UsageRecord = z.infer<typeof usageRecordSchema>

export const subscriptionSchema = z.object({
  id: z.string(),
  status: z
    .enum([
      'active',
      'canceled',
      'incomplete',
      'incomplete_expired',
      'past_due',
      'paused',
      'trialing',
      'unpaid',
    ])
    .nullable(),
  customerId: z.string(),
  customerEmail: z.string().email().nullable(),
  canceledAt: z.string().nullable(),
  currentPeriodStartAt: z.string(),
  currentPeriodEndAt: z.string(),
  created: z.number().optional(),
})

export type Subscription = z.infer<typeof subscriptionSchema>

export const overagesSubscriptionSchema = subscriptionSchema.extend({
  usageRecords: z.object({
    blocks: usageRecordSchema.omit({ status: true }),
    apiRequests: usageRecordSchema,
    assetStorage: usageRecordSchema.nullish(),
    assetApiRequests: usageRecordSchema.nullish(),
  }),
})

export type OveragesSubscription = z.infer<typeof overagesSubscriptionSchema>

const planV2Schema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('personal'),
    overagesSubscription: overagesSubscriptionSchema.nullish(),
  }),
  z.object({
    type: z.literal('team'),
    overagesSubscription: overagesSubscriptionSchema,
  }),
  z.object({
    type: z.literal('team_v2'),
    subscription: subscriptionSchema,
    overagesSubscription: overagesSubscriptionSchema,
  }),
  z.object({
    type: z.literal('unlimited'),
    subscription: subscriptionSchema,
    overagesSubscription: overagesSubscriptionSchema,
  }),
])

export type PlanV2 = z.infer<typeof planV2Schema>

/* -------------------------------------------------------------------------- */
/*                                 Team Schema                                */
/* -------------------------------------------------------------------------- */

export const teamSchema = baseSchema.extend({
  title: z.string(),
  slug: z.string(),
  repoIds: z.array(z.string()),
  members: z.array(memberSchema),
  avatarUrl: z.string().nullable(),
  description: z.string().nullable(),
  invites: z.array(teamInviteSchema),
  createdAt: z.string(),
  plan: z.enum(['personal', 'pro']).nullish(), // deprecated
  plan_v2: planV2Schema.optional(),
})

export type Team = z.infer<typeof teamSchema>
export type TeamUpdate = Update<Team>

export const teamBaseOps = generate('team', teamSchema.parse)
